import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import {
  formatRequestedDuration,
  getRequesterName,
  useAwaitingAccessRequests,
} from '~components/organization/AlertsMenu/useAwaitingAccessRequests';
import { WarningIcon } from '~components/ui/icons';
import { CONTAINER_MAX_WIDTH, CONTAINER_PADDING } from '~components/ui/PageContent';

const ACCESS_REQUESTS_PATH = 'settings/access-requests';

/**
 * Pinned to the bottom of the page while the organization has an access request awaiting its
 * Owner's decision. Shows the most recent one; the Alerts menu lists them all.
 */
export function AccessRequestBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { organization } = useAuth();
  const { awaitingAccessRequests } = useAwaitingAccessRequests({ poll: false });

  const [latest] = [...awaitingAccessRequests].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );

  // On the Access Requests page itself the request is already in front of them.
  if (!latest || !organization || location.pathname.endsWith(ACCESS_REQUESTS_PATH)) {
    return null;
  }

  return (
    <Box
      position="sticky"
      bottom="0"
      zIndex="sticky"
      width="100%"
      bgColor="orange.50"
      borderTop="1px solid"
      borderColor="orange.100"
    >
      <HStack
        maxW={CONTAINER_MAX_WIDTH}
        marginX="auto"
        paddingX={CONTAINER_PADDING}
        paddingY="4"
        spacing="4"
      >
        <WarningIcon width="6" height="6" color="orange.400" />
        <Box flex="1" color="orange.800">
          <Text fontWeight="medium">{t('accessRequestBannerTitle')}</Text>
          <Text fontSize="sm">
            {t('accessRequestBannerMessage', {
              name: getRequesterName(latest.requestingServiceAdmin),
              duration: formatRequestedDuration(latest.requestedDurationMinutes),
            })}
          </Text>
        </Box>
        <Button
          variant="outline"
          colorScheme="orange"
          backgroundColor="white"
          onClick={() => navigate(`/${organization.handle}/${ACCESS_REQUESTS_PATH}`)}
        >
          {t('viewDetails')}
        </Button>
      </HStack>
    </Box>
  );
}
